import React from 'react';
import { AddOnPriceDetails } from 'types/expiryRenewal';
import { isAddOnPricedMonthly } from '../../utils/pricingUtils';
import RenewalPrice from '../atoms/RenewalPrice';

export type FooterContext = {
  productCode?: string;
  // The member is already on a monthly long term policy - frequency is fixed.
  isMonthlyLongerTermPolicy?: boolean;
  // The member has taken up the Monthly Flex offer.
  monthlyFlex?: boolean;
  coverPeriod?: number;
};

/**
 * The price block that sits in a checkbox option's footer slot. An extra being added and an extra being
 * removed show the same figure in the same markup - the only difference is which list it
 * sits in - so the field choice and the period suffix are made once here rather than
 * duplicated per call site.
 *
 * The discounted fields double as the "is there a discount" test: the backend only sends
 * them when one applies, so a missing value means show the full price with no strikethrough
 * rather than show zero.
 */
interface AddOnPriceFooterProps {
  details: AddOnPriceDetails;
  context: FooterContext;
}

const AddOnPriceFooter = ({
  details: {
    price, discountedPrice, monthlyPrice, monthlyDiscountedPrice,
  },
  context: {
    productCode, isMonthlyLongerTermPolicy, monthlyFlex, coverPeriod,
  },
}: AddOnPriceFooterProps) => {
  const useMonthlyPrice = isAddOnPricedMonthly(productCode, { isMonthlyLongerTermPolicy, monthlyFlex });

  const nowPrice = useMonthlyPrice
    ? (monthlyDiscountedPrice ?? monthlyPrice ?? 0)
    : (discountedPrice ?? price ?? 0);
  const wasPrice = useMonthlyPrice
    ? (monthlyDiscountedPrice && (monthlyPrice ?? 0))
    : (discountedPrice && (price ?? 0));

  // A policy paid upfront with a cover period other than 12 months isn't billed per year,
  // so the /year suffix would be misleading here.
  const isNotAnnual = coverPeriod !== undefined && coverPeriod !== 12;
  const yearOrBlankLabel = isNotAnnual ? '' : '/year';
  const periodLabel = useMonthlyPrice ? '/month' : yearOrBlankLabel;

  return (
    <div className="mt-3">
      <RenewalPrice price={nowPrice} period={periodLabel} wasPrice={wasPrice} />
    </div>
  );
};

export default AddOnPriceFooter;
